import React from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { downloadArtifact } from './api';

// What an approved class has actually been built out of, on the concept page.
//
// The concept page used to say nothing about artifacts at all: an approved class
// showed a Production section with a single button, so the only way to learn
// whether a script existed was to open the workspace and look. The internal team
// reviews classes from here, so the inventory belongs here.
//
// Editing deliberately does NOT happen here. The editor, the JPRO steering chat,
// image governance and the quiz builder all live in ArtifactDetail, and a second
// editing surface would be a second set of rules about what may be changed and
// when. Clicking through opens the workspace with that artifact already selected,
// so this stays an index and the workspace stays the one place work happens.
const isWriting = (a) => a?.status === 'generating' && !a.generation_stalled;
const isStopped = (a) => a?.status === 'failed' || Boolean(a?.generation_stalled);

const statusLabel = (a) => {
  if (isWriting(a)) return 'writing…';
  if (a.generation_stalled) return 'stopped';
  return a.status;
};

const statusTone = (a) => {
  if (a.status === 'approved') return 'text-green-400';
  if (a.status === 'written_back') return 'text-blue-400';
  if (isStopped(a)) return 'text-red-400';
  if (isWriting(a)) return 'text-[#5ea9ff]';
  return 'text-[#8a95a8]';
};

export default function ConceptArtifacts({ production, onOpen, onAdd }) {
  const artifacts = production?.artifacts || [];

  const downloadMut = useMutation({
    mutationFn: (a) => downloadArtifact(a.id, a.title),
    onError: (e) => toast.error(e?.message || 'Could not download that artifact'),
  });

  if (!production) {
    return (
      <div className="text-[11px] text-[#7d8aa0] mb-2">
        Approved for development. Nothing has been produced yet.
      </div>
    );
  }

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wide text-[#7d8aa0]">
          Artifacts · {artifacts.length || 'none'}
        </span>
        <span className="text-[10px] text-[#5f6b7e]">
          {String(production.status).replace(/_/g, ' ')}
        </span>
      </div>

      {artifacts.length === 0 ? (
        <div className="text-[11px] text-[#7d8aa0]">
          Nothing produced yet. Open the workspace to build the script and quiz.
        </div>
      ) : (
        artifacts.map((a) => (
          <div key={a.id} className="flex items-center gap-2 py-1 border-b border-[#161e2a] last:border-0">
            <button onClick={() => onOpen(a.id)}
              className="flex-1 min-w-0 text-left group">
              <div className="text-[11px] text-[#c7d0e0] group-hover:text-white truncate">
                {a.title || a.kind}
              </div>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-[#7d8aa0]">{a.kind}</span>
                <span className={statusTone(a)}>{statusLabel(a)}</span>
              </div>
            </button>
            {/* Download is the one action that makes sense from an index: it needs
                no editing context and it is how the team gets the .docx for the
                manual LMS upload. Anything that CHANGES the artifact goes through
                the workspace. A generating or failed row has nothing to render
                yet, so it gets no download button rather than a broken one. */}
            {!isWriting(a) && !isStopped(a) && (
              <button onClick={() => downloadMut.mutate(a)} disabled={downloadMut.isPending}
                className="text-[10px] text-[#5ea9ff] disabled:opacity-40 shrink-0">
                .docx
              </button>
            )}
            <button onClick={() => onOpen(a.id)}
              className="text-[10px] text-[#5ea9ff] shrink-0">Edit</button>
          </div>
        ))
      )}

      <button onClick={onAdd} className="mt-2 text-[11px] text-[#5ea9ff]">
        + Add an artifact
      </button>
    </div>
  );
}
